/*
 * g-serp-license.js — SERP Simulator Pro licensing client
 *
 * Replaces the previous scheme, in which Pro was granted by a client-side
 * boolean (`localStorage.itech_pro_unlocked`) and a single globally-shared
 * passcode fetched in base64 from /pages/gfsr. Both were trivially bypassable.
 *
 * Here, Pro is derived from a server-signed token bound to one device. A user
 * editing localStorage produces an invalid signature, not Pro.
 *
 * Threat model, honestly: this is client-side JavaScript and a determined
 * developer can patch the bundle in their own browser. What this buys is that
 * keys are per-buyer, sharing is attributable and costs the sharer their seats,
 * and revocation is a status flip rather than a redeploy.
 *
 * Loaded before g-serp-app.js. Owns no UI; g-serp-app.js reads itvApp.isPro,
 * which delegates here.
 */
(function (window) {
    'use strict';

    var CFG = window.ITV_LICENSE_CONFIG || {};

    var STORE_KEY = 'itv_serp_license';

    // Grace: if the licence server cannot be REACHED, an already-valid token
    // keeps working this long past its last successful verify. Being unable to
    // ask is not the same as being told no — see verify() below.
    var GRACE_DAYS = 7;

    /*
     * Public half of the licence signing key (ECDSA P-256 / ES256).
     *
     * Safe to publish by construction: it verifies signatures and cannot
     * produce them. The private half never leaves the Edge Function env.
     *
     * This exists so the grace window can verify a token OFFLINE. An earlier
     * draft granted grace on the mere PRESENCE of a token plus a stored
     * `lastVerifiedAt` — both attacker-controlled, so
     *     localStorage.itv_serp_license =
     *       JSON.stringify({token:"x", lastVerifiedAt: Date.now()})
     * plus blocking the licence host granted Pro indefinitely. One line in
     * devtools, i.e. exactly the bypass this rewrite existed to close, just
     * moved to a new storage key. Now a forged record fails signature
     * verification and grace is refused.
     *
     * DEPLOY: replace with the real public JWK when the keypair is generated.
     * While the placeholder is in place, offline grace fails closed.
     */
    var PUBLIC_JWK = window.ITV_LICENSE_PUBLIC_JWK || null;

    /*
     * Legacy acceptance is handled ENTIRELY SERVER-SIDE — there is deliberately
     * no legacy branch in this file.
     *
     * An earlier draft kept the old /pages/gfsr check here, gated on a hardcoded
     * expiry date and only consulted when the licence server was unreachable.
     * That was defeatable: block the licence domain to force the "unreachable"
     * path, roll the system clock back to reopen the window, and the legacy code
     * works forever. A client clock cannot gate anything a client wants to
     * bypass.
     *
     * Instead the old shared passcode is seeded into the licences table as an
     * ordinary row with a real `expires_at` and a high seat limit (see
     * seed-legacy-key.md). It therefore flows through /activate like any other
     * key, and its expiry, revocation and seat behaviour are all server-side and
     * adjustable without a deploy. Nothing in the browser decides when the
     * migration window closes.
     */

    // ---------------------------------------------------------------------
    // Storage
    // ---------------------------------------------------------------------

    function load() {
        try {
            var raw = window.localStorage.getItem(STORE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;   // private mode, blocked storage, corrupt JSON
        }
    }

    function save(rec) {
        try {
            window.localStorage.setItem(STORE_KEY, JSON.stringify(rec));
        } catch (e) { /* non-fatal: the session still works, it just won't persist */ }
    }

    function clear() {
        try {
            window.localStorage.removeItem(STORE_KEY);
            // Legacy keys from the previous scheme. Removed unconditionally so a
            // returning customer cannot be re-granted Pro by stale local state.
            window.localStorage.removeItem('itech_pro_unlocked');
            window.localStorage.removeItem('saved_passcode');
        } catch (e) { /* ignore */ }
    }

    // ---------------------------------------------------------------------
    // Device fingerprint
    // ---------------------------------------------------------------------

    /*
     * A stable-enough per-browser id. Deliberately NOT a real fingerprint: we
     * want seat counting, not tracking. Random, generated once, stored locally.
     * Clearing storage costs the user a seat until it is released, which is why
     * seats are releasable and the limit is server-side adjustable.
     */
    function deviceId() {
        var rec = load() || {};
        if (rec.device) return rec.device;

        var id;
        if (window.crypto && window.crypto.randomUUID) {
            id = window.crypto.randomUUID();
        } else if (window.crypto && window.crypto.getRandomValues) {
            var b = new Uint8Array(16);
            window.crypto.getRandomValues(b);
            id = Array.prototype.map.call(b, function (x) {
                return ('0' + x.toString(16)).slice(-2);
            }).join('');
        } else {
            id = 'fallback-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        }

        rec.device = id;
        save(rec);
        return id;
    }

    function deviceLabel() {
        var ua = navigator.userAgent || '';
        var os = /Windows/.test(ua) ? 'Windows'
               : /Android/.test(ua) ? 'Android'
               : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
               : /Mac OS X/.test(ua) ? 'macOS'
               : /Linux/.test(ua) ? 'Linux' : 'Unknown';
        var br = /Edg\//.test(ua) ? 'Edge'
               : /Chrome\//.test(ua) ? 'Chrome'
               : /Firefox\//.test(ua) ? 'Firefox'
               : /Safari\//.test(ua) ? 'Safari' : 'Browser';
        return br + ' on ' + os;
    }

    // ---------------------------------------------------------------------
    // Transport
    // ---------------------------------------------------------------------

    function configured() {
        return !!(CFG.url && CFG.anonKey);
    }

    function call(fn, body) {
        if (!configured()) {
            return Promise.reject({ unreachable: true, reason: 'not_configured' });
        }
        return window.fetch(CFG.url.replace(/\/+$/, '') + '/functions/v1/' + fn, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'apikey': CFG.anonKey,
                'authorization': 'Bearer ' + CFG.anonKey
            },
            body: JSON.stringify(body)
        }).then(function (res) {
            // 5xx is OUR failure, not a refusal. Surface it as unreachable so the
            // caller falls into the grace window instead of locking the user out.
            if (res.status >= 500) {
                return Promise.reject({ unreachable: true, reason: 'server_error' });
            }
            return res.json().catch(function () {
                return Promise.reject({ unreachable: true, reason: 'bad_response' });
            }).then(function (data) {
                return { status: res.status, data: data };
            });
        }, function () {
            // Network-level failure: offline, DNS, CORS, blocked host.
            return Promise.reject({ unreachable: true, reason: 'network' });
        });
    }

    // ---------------------------------------------------------------------
    // State
    // ---------------------------------------------------------------------

    var pro = false;   // the single source of truth g-serp-app.js reads

    // ---------------------------------------------------------------------
    // Offline token verification
    // ---------------------------------------------------------------------

    function b64urlBytes(s) {
        var p = String(s).replace(/-/g, '+').replace(/_/g, '/');
        while (p.length % 4) p += '=';
        var bin = window.atob(p);
        var out = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
    }

    /**
     * Verifies the token's ES256 signature against the embedded public key and
     * returns its claims, or null. Purely local — no network.
     */
    function verifyOffline(token) {
        if (!PUBLIC_JWK || !token || !window.crypto || !window.crypto.subtle) {
            return Promise.resolve(null);
        }
        var parts = String(token).split('.');
        if (parts.length !== 3) return Promise.resolve(null);

        return window.crypto.subtle.importKey(
            'jwk', PUBLIC_JWK,
            { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']
        ).then(function (key) {
            return window.crypto.subtle.verify(
                { name: 'ECDSA', hash: { name: 'SHA-256' } },
                key,
                b64urlBytes(parts[2]),
                new TextEncoder().encode(parts[0] + '.' + parts[1])
            );
        }).then(function (ok) {
            if (!ok) return null;
            try {
                return JSON.parse(new TextDecoder().decode(b64urlBytes(parts[1])));
            } catch (e) {
                return null;
            }
        })['catch'](function () { return null; });
    }

    /**
     * Grace is granted only against a GENUINELY SIGNED, unexpired token.
     *
     * `lastVerifiedAt` is still consulted, but it can only SHORTEN the window:
     * the hard bound is the signed `exp` claim, which cannot be forged without
     * the private key. So rolling the system clock forward or editing the
     * stored record cannot mint access — at most it declines it.
     */
    function withinGrace(rec) {
        if (!rec || !rec.token) return Promise.resolve(false);

        return verifyOffline(rec.token).then(function (claims) {
            if (!claims || !claims.exp) return false;

            var now = Date.now();
            if (now >= claims.exp * 1000) return false;   // genuinely expired

            // Advisory cap. Forging it forward buys nothing beyond `exp`.
            if (rec.lastVerifiedAt &&
                (now - rec.lastVerifiedAt) >= GRACE_DAYS * 86400000) return false;

            return true;
        });
    }

    var ITVLicense = {

        isPro: function () { return pro; },

        tier: function () {
            var rec = load();
            return (rec && rec.tier) || null;
        },

        /* True when the user is running on an unverified but still-granted token,
           i.e. we could not reach the server. Lets the UI say so honestly. */
        isStale: function () {
            var rec = load();
            return pro && !!rec && !!rec.lastVerifiedAt &&
                   (Date.now() - rec.lastVerifiedAt) > 86400000;
        },

        /**
         * Boot check. Never rejects — the tool must load regardless.
         * Resolves to true if Pro is granted for this session.
         */
        boot: function () {
            var rec = load();

            if (!rec || !rec.token) {
                pro = false;
                clear();                // sweep any legacy keys left behind
                return Promise.resolve(false);
            }

            return call('verify', { token: rec.token }).then(function (out) {
                var d = out.data || {};

                if (d.valid) {
                    pro = true;
                    rec.tier = d.tier || rec.tier;
                    if (d.refreshed) rec.token = d.refreshed;
                    rec.lastVerifiedAt = Date.now();
                    save(rec);
                    return true;
                }

                // An explicit refusal. Lock immediately — do NOT fall into grace.
                pro = false;
                rec.revokedReason = d.reason || 'invalid';
                clear();
                return false;

            }, function (err) {
                // Could not ask. Honour the grace window — but only against a
                // signature we can verify locally.
                if (!err || !err.unreachable) {
                    pro = false;
                    return false;
                }
                return withinGrace(rec).then(function (ok) {
                    pro = ok;
                    return ok;
                });
            });
        },

        /**
         * Exchange a customer key for a device-bound token.
         * Resolves { ok:true } or { ok:false, error, message }.
         */
        activate: function (key) {
            var trimmed = (key || '').trim();
            if (!trimmed) {
                return Promise.resolve({ ok: false, error: 'bad_request', message: 'Enter your key first.' });
            }

            return call('activate', {
                key: trimmed,
                device_hash: deviceId(),
                platform: 'web',
                device_label: deviceLabel()
            }).then(function (out) {
                var d = out.data || {};

                if (out.status === 200 && d.token) {
                    var rec = load() || {};
                    rec.token = d.token;
                    rec.tier = d.tier;
                    rec.device = rec.device || deviceId();
                    rec.lastVerifiedAt = Date.now();
                    save(rec);
                    try {
                        window.localStorage.removeItem('itech_pro_unlocked');
                        window.localStorage.removeItem('saved_passcode');
                    } catch (e) { /* ignore */ }
                    pro = true;
                    return {
                        ok: true,
                        tier: d.tier,
                        seats: d.seats,
                        // Server-supplied. Set on the seeded legacy row so
                        // existing customers are told their code is changing.
                        message: d.notice || undefined
                    };
                }

                // Distinct, actionable messages. The old code showed one generic
                // "Invalid Passcode" toast for every failure mode.
                return {
                    ok: false,
                    error: d.error || 'invalid_key',
                    message: d.message || 'That key could not be verified.',
                    seats: d.seats
                };

            }, function () {
                // Unreachable. No local fallback by design — see the note on
                // legacy acceptance at the top of this file.
                return { ok: false, error: 'unreachable',
                         message: "Couldn't reach the licence server. Check your connection and try again." };
            });
        },

        /** Release this device's seat, so the user can activate elsewhere. */
        signOut: function () {
            var rec = load();
            pro = false;
            clear();
            if (rec && rec.token) {
                // Best effort. A failed release leaves the seat until it is
                // reaped server-side; it must never block the local sign-out.
                // Folded into /verify rather than a third function, per the
                // two-endpoint constraint.
                call('verify', { token: rec.token, release: true })['catch'](function () {});
            }
            return Promise.resolve();
        }
    };

    /*
     * ---- Tamper friction, NOT security -----------------------------------
     *
     * Be clear about what this is. The whole tool runs in the visitor's
     * browser, so its Pro gates are on the attacker's own machine and cannot
     * be made safe. What CAN be removed is the one-line console bypass, which
     * is the realistic threat: someone who asks an AI "how do I unlock this"
     * and pastes the answer.
     *
     * Freezing the surface turns `ITVLicense.isPro = () => true` and
     * `window.ITVLicense = {...}` into no-ops (silently in sloppy mode, a
     * TypeError in strict). Anyone willing to go further — a userscript, an
     * extension, a proxy that rewrites this file — still wins, and always
     * will. That population was never going to pay.
     *
     * `pro` itself is a closure variable and was never reachable from the
     * console; this only closes the door on replacing the accessors around it.
     *
     * Nothing legitimate writes these: the app only ever READS
     * window.ITVLicense and calls its methods.
     */
    Object.freeze(ITVLicense);
    try {
        Object.defineProperty(window, 'ITVLicense', {
            value: ITVLicense,
            writable: false,
            configurable: false,
            enumerable: true,
        });
    } catch (e) {
        // Never let hardening break the tool. If the property cannot be
        // redefined, fall back to a plain assignment: the licence check still
        // works, it is simply overridable again.
        window.ITVLicense = ITVLicense;
    }

})(window);
